import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { ArrowLeft, Focus, Minus, Plus, X } from "lucide-react";
import "./publisherAtlas.css";

const palette = ["#ff5342", "#fe6d4e", "#ff9463", "#ffc28b", "#819898", "#4c7d8e", "#174d7c", "#2794c3"];
const percent = (value) => `${(value * 100).toFixed(3)}%`;

function updateAtlasLabels(svg, scale) {
  d3.select(svg).selectAll("text[data-label-radius]")
    .attr("display", function () {
      return Number(this.dataset.labelRadius) * scale >= Number(this.dataset.labelThreshold) ? null : "none";
    })
    .attr("font-size", function () {
      const radius = Number(this.dataset.labelRadius);
      const fit = this.dataset.labelFit ? radius * 1.8 / Math.max(1, this.textContent.length * 0.56) : radius * 0.38;
      return Math.min(Number(this.dataset.labelFont) / scale, fit);
    });
}

export default function PublisherAtlas({ games }) {
  const hostRef = useRef(null);
  const svgRef = useRef(null);
  const layerRef = useRef(null);
  const zoomRef = useRef(null);
  const [size, setSize] = useState({ width: 1200, height: 760 });
  const [selectedName, setSelectedName] = useState(null);
  const [hoveredName, setHoveredName] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      setSize({ width: Math.max(1, entry.contentRect.width), height: Math.max(1, entry.contentRect.height) });
    });
    observer.observe(hostRef.current);
    return () => observer.disconnect();
  }, []);

  const portfolios = useMemo(() => {
    const grouped = d3.group(games.filter((game) => Number(game.mindshare ?? game.views) > 0), (game) => String(game.publisher || "Unknown publisher").trim());
    const rows = Array.from(grouped, ([name, titles]) => ({
      name,
      games: [...titles].sort((first, second) => Number(second.mindshare ?? second.views) - Number(first.mindshare ?? first.views)),
      value: d3.sum(titles, (game) => Number(game.mindshare ?? game.views)),
    })).sort((first, second) => second.value - first.value);
    const root = d3.hierarchy({ children: rows }).sum((row) => row.value || 0);
    d3.treemap().tile(d3.treemapSquarify).size([size.width, size.height]).paddingOuter(8)(root);
    return root.leaves().map((node, index) => {
      const width = node.x1 - node.x0;
      const height = node.y1 - node.y0;
      const radius = Math.max(0.1, Math.min(width, height) / 2);
      if (node.data.games.length === 1) {
        return { ...node.data, x: (node.x0 + node.x1) / 2, y: (node.y0 + node.y1) / 2, radius, width, height, hubX: 0, hubY: 0, hubRadius: radius * 0.96, color: palette[index % palette.length], satellites: [] };
      }
      const orbit = d3.hierarchy({ children: [{ hub: true, value: node.data.value }, ...node.data.games.map((game) => ({ game, value: Number(game.mindshare ?? game.views) }))] }).sum((row) => row.value || 0);
      d3.pack().size([1000, 1000]).padding(2)(orbit);
      const localScale = Math.min(width, height) * 0.96 / 1000;
      const hub = orbit.leaves().find((leaf) => leaf.data.hub);
      return { ...node.data, x: (node.x0 + node.x1) / 2, y: (node.y0 + node.y1) / 2, radius, width, height, hubX: (hub.x - 500) * localScale, hubY: (hub.y - 500) * localScale, hubRadius: hub.r * localScale, color: palette[index % palette.length], satellites: orbit.leaves().filter((leaf) => !leaf.data.hub).map((leaf) => ({ ...leaf.data, x: (leaf.x - 500) * localScale, y: (leaf.y - 500) * localScale, radius: leaf.r * localScale })) };
    });
  }, [games, size]);

  const selected = portfolios.find((portfolio) => portfolio.name === selectedName);
  const focusPortfolio = (portfolio) => {
    setSelectedName(portfolio?.name || null);
    setSelectedGame(null);
    const scale = portfolio ? Math.min(8, size.width * 0.65 / Math.max(1, portfolio.width * 1.15), size.height / Math.max(1, portfolio.height * 1.15)) : 1;
    const transform = portfolio ? d3.zoomIdentity.translate(size.width * 0.42, size.height / 2).scale(scale).translate(-portfolio.x, -portfolio.y) : d3.zoomIdentity;
    const svg = d3.select(svgRef.current);
    svg.interrupt();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) svg.call(zoomRef.current.transform, transform);
    else svg.transition().duration(420).ease(d3.easeCubicInOut).call(zoomRef.current.transform, transform);
  };

  useEffect(() => {
    const zoom = d3.zoom().scaleExtent([0.5, 18]).on("zoom", ({ transform }) => {
      d3.select(layerRef.current).attr("transform", transform);
      updateAtlasLabels(svgRef.current, transform.k);
    });
    zoomRef.current = zoom;
    const svg = d3.select(svgRef.current);
    svg.call(zoom).on("dblclick.zoom", null);
    return () => { svg.interrupt(); svg.on(".zoom", null); };
  }, []);

  useEffect(() => {
    setSelectedName(null);
    setSelectedGame(null);
    if (zoomRef.current) d3.select(svgRef.current).call(zoomRef.current.transform, d3.zoomIdentity);
  }, [portfolios]);

  useEffect(() => {
    updateAtlasLabels(svgRef.current, d3.zoomTransform(svgRef.current).k);
  }, [portfolios, selectedName, hoveredName]);

  return <section ref={hostRef} className="publisher-atlas" aria-label="Publisher Atlas">
    <header className="publisher-atlas-heading"><span>Publisher Atlas</span><small>{portfolios.length.toLocaleString()} publishers / {games.length.toLocaleString()} games</small></header>
    <div className="publisher-atlas-navigation">
      {selected && <button onClick={() => focusPortfolio(null)} title="All publishers" aria-label="All publishers"><ArrowLeft size={17} /></button>}
      <button onClick={() => focusPortfolio(selected)} title="Fit view" aria-label="Fit publisher view"><Focus size={17} /></button>
      <button onClick={() => d3.select(svgRef.current).call(zoomRef.current.scaleBy, 1.35)} title="Zoom in" aria-label="Zoom in"><Plus size={17} /></button>
      <button onClick={() => d3.select(svgRef.current).call(zoomRef.current.scaleBy, 1 / 1.35)} title="Zoom out" aria-label="Zoom out"><Minus size={17} /></button>
    </div>
    <svg ref={svgRef} width={size.width} height={size.height} aria-label="Publisher portfolios and connected games">
      <g ref={layerRef}>{portfolios.map((portfolio) => {
        const active = selectedName === portfolio.name || hoveredName === portfolio.name;
        const visibleGames = portfolio.satellites;
        const singleGame = portfolio.games.length === 1;
        const label = singleGame ? `${portfolio.name} (${portfolio.games[0].name})` : portfolio.name.slice(0, 24);
        return <g key={portfolio.name} transform={`translate(${portfolio.x},${portfolio.y})`} opacity={selectedName && !active ? 0.15 : 1}>
          {!singleGame && <ellipse rx={portfolio.width / 2} ry={portfolio.height / 2} fill="#a3adae" fillOpacity={active ? 0.065 : 0.025} pointerEvents="none" />}
          <g className="publisher-atlas-spokes" pointerEvents="none">
            {visibleGames.map((satellite) => {
              const deltaX = satellite.x - portfolio.hubX;
              const deltaY = satellite.y - portfolio.hubY;
              const distance = Math.hypot(deltaX, deltaY) || 1;
              const directionX = deltaX / distance;
              const directionY = deltaY / distance;
              return <line key={satellite.game.id ?? satellite.game.name} x1={portfolio.hubX + directionX * portfolio.hubRadius} y1={portfolio.hubY + directionY * portfolio.hubRadius} x2={satellite.x - directionX * satellite.radius} y2={satellite.y - directionY * satellite.radius} stroke={portfolio.color} strokeOpacity={active ? 0.4 : 0.2} strokeWidth={0.7} />;
            })}
          </g>
          {visibleGames.map((satellite) => <g key={satellite.game.id ?? satellite.game.name}>
            <circle cx={satellite.x} cy={satellite.y} r={satellite.radius} fill="#000" pointerEvents="none" />
            <circle data-game-value={satellite.value} cx={satellite.x} cy={satellite.y} r={satellite.radius} fill={portfolio.color} fillOpacity={active ? 0.75 : 0.45} stroke="#070a0b" strokeWidth={0.7} onClick={() => { if (selectedName !== portfolio.name) focusPortfolio(portfolio); else setSelectedGame(satellite.game); }} style={{ cursor: "pointer" }}><title>{satellite.game.name}: {percent(satellite.value)}</title></circle>
            <text data-label-radius={satellite.radius} data-label-threshold="16" data-label-font="12" x={satellite.x} y={satellite.y} textAnchor="middle" dy="0.35em" fill="#fff" stroke="#000" strokeWidth={0.25} paintOrder="stroke" pointerEvents="none">{satellite.game.name.slice(0, 23)}</text>
          </g>)}
          <g transform={`translate(${portfolio.hubX},${portfolio.hubY})`} role="button" tabIndex={0} aria-label={`Explore ${portfolio.name}`} onClick={() => focusPortfolio(portfolio)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); focusPortfolio(portfolio); } }} onMouseEnter={() => setHoveredName(portfolio.name)} onMouseLeave={() => setHoveredName(null)} style={{ cursor: "pointer" }}>
            <circle data-portfolio-value={portfolio.value} r={portfolio.hubRadius} fill={portfolio.color} stroke={active ? "#fff" : "#11191b"} strokeWidth={active ? 1.3 : 0.8}><title>{portfolio.name}: {percent(portfolio.value)} across {portfolio.games.length} games</title></circle>
            <text data-label-radius={portfolio.hubRadius} data-label-threshold="18" data-label-font="14" data-label-fit={singleGame ? "true" : undefined} textAnchor="middle" dy="0.35em" fill="#fff" stroke="#080b0c" strokeWidth={0.3} paintOrder="stroke" pointerEvents="none">{label}</text>
          </g>
        </g>;
      })}</g>
    </svg>
    {!portfolios.length && <div className="publisher-atlas-empty">No portfolios match these filters.</div>}
    {selected && <aside className="publisher-atlas-details">
      <div className="publisher-atlas-detail-title"><h2>{selected.name}</h2><button onClick={() => focusPortfolio(null)} title="Close portfolio" aria-label="Close portfolio"><X size={16} /></button></div>
      <div className="publisher-atlas-totals"><strong>{percent(selected.value)}</strong><span>{selected.games.length} games</span></div>
      {selectedGame && <div className="publisher-atlas-game">{selectedGame.imageUrl && <img src={selectedGame.imageUrl} alt="" />}<strong>{selectedGame.name}</strong><span>{percent(Number(selectedGame.mindshare ?? selectedGame.views))}</span></div>}
      <ol>{selected.games.map((game) => <li key={game.id ?? game.name}><button onClick={() => setSelectedGame(game)} aria-pressed={selectedGame?.name === game.name}><span>{game.name}</span><strong>{percent(Number(game.mindshare ?? game.views))}</strong></button></li>)}</ol>
    </aside>}
  </section>;
}